import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdCheckbox,
  CmdCheckboxGroup,
  CmdChipSelect,
  CmdModal,
  CmdModalFooterButton,
  CmdRadioButton,
  CmdSwitch,
} from '@commander-services/gui-components';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { useRoute } from 'wouter';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../../Services/FormikValidatorService';
import { IOption } from '../../Forms/CmdField';
import * as ExportState from '../../Export/ExportState';
import { reportSettingsOptionsR05 } from '../../Reports/ReportSettings';
import showMessage from '../../Toastr/ToastService';
import { IDownload } from '../../Export/ExportState';
import { REPORT_05 } from '../../Reports/constants';
import { IMessageReportCreated } from '../../SocketConnection/types';
import Calendar from '../../Forms/ReactHookFormFields/Calendar';
import * as ReportService from '../../Reports/ReportService';
import { transformErrors } from '../../../Services/HookFormService';
import { selectedVehiclesAtom } from '../../../store/recoil/vehicles';
import { IGetVehiclesData, IRidebookDownloadData } from '../interfaces';
import { getRequest, postRequest } from '../../../Services/HttpService';

interface IFormProps {
  closeCallback: () => void;
  fileName: string;
  actionUrl: string;
  formats: string[];
  dates: Date[] | null;
}

interface IRideBookInputForm {
  reportId: string;
  format: string;
  date: Date[];
  reportSettings: string[];
  vehicleIds: string[];
  showEmpty: number;
}

const MAX_MONTH_NUMBER = 3;

export default function RidebookForm(props: IFormProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [, params] = useRoute('/overview-tours/:vehicleId');
  const [vehicleOptions, setVehicleOptions] = React.useState<IOption[]>([]);
  const [loader, setLoader] = React.useState<boolean>(false);
  const setIsActiveExport = useSetRecoilState<false | string>(ExportState.isActive);
  const [exportData, setExportData] = useRecoilState<IMessageReportCreated | null>(
    ExportState.data
  );
  const [reportRange, setReportRange] = React.useState('');
  const [reportActionUrl, setReportActionUrl] = React.useState('');
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const getReportSettingsOptions = () => {
    const headers = ['basicHeader', 'customerHeader', 'addressHeader', 'billingHeader'];
    const others = reportSettingsOptionsR05.filter((item) => !headers.includes(item));

    return {
      headers: headers.map((item) => ({
        value: item,
        item: f({ id: `reports.R05.${item}` }),
        disabled: item === 'basicHeader',
        notHidden: true,
      })),
      others: others.map((item) => ({
        value: item,
        item: f({ id: `reports.R05.${item}` }),
        disabled: item === 'rideBook',
        notHidden: true,
      })),
    };
  };

  const defaultCheckboxValue = () => {
    if (reportSettingsOptionsR05.length === 13) {
      return 'checked';
    }
    if (reportSettingsOptionsR05.length > 0 && reportSettingsOptionsR05.length < 13) {
      return 'indeterminate';
    }
    return '';
  };

  const reportSettingsOptions = getReportSettingsOptions();
  const [valuesJsons, setValuesJsons] = useRecoilState<string[]>(ExportState.valuesJsons);
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const setDownloads = useSetRecoilState<IDownload[]>(ExportState.downloads);
  const [bossCheckboxValue, setBossCheckboxValue] = React.useState(defaultCheckboxValue());

  const getSelectedVehicles = (): string[] => {
    if (!params && selectedVehicles.length > 0) {
      return selectedVehicles.map((i) => String(i));
    }
    if (params && params.vehicleId) {
      return [params.vehicleId];
    }
    return [];
  };

  const getRefDate = (toDateValue: Date) => {
    const date = new Date(toDateValue);
    const newDate = dayjs(date)
      .set('second', 0)
      .set('millisecond', 0)
      .set('minute', 0)
      .set('hour', 24)
      .toDate();
    return new Date(newDate.setMonth(newDate.getMonth() - MAX_MONTH_NUMBER));
  };

  const checkValidation = (from: Date, to: Date) => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const refDate = getRefDate(toDate);
    if (refDate.getTime() > fromDate.getTime()) {
      return refDate;
    }
    return from;
  };

  const parseDateToday0 = new Date(new Date().setHours(0, 0, 0, 0));
  const parseDateToday24 = new Date(new Date().setHours(23, 59, 0, 0));

  const validateFields = (values: IRideBookInputForm) => {
    let formErrors: any = {};
    if (!validation) {
      return formErrors;
    }
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    const message = ReportService.handleDatepickerValidation(values?.date, '5', reportRange);
    if (message.length > 0) {
      formErrors.date = f({
        id: message,
      });
    }
    return transformErrors(formErrors);
  };

  const getDate = (): Date[] => {
    const dateFrom =
      props.dates && props.dates[0] && props.dates[1]
        ? checkValidation(props.dates[0], props.dates[1])
        : parseDateToday0;
    const dateTo = props.dates && props.dates[1] ? props.dates[1] : parseDateToday24;
    return [new Date(dateFrom), new Date(dateTo)];
  };

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<IRideBookInputForm>({
    mode: 'onChange',
    defaultValues: {
      date: getDate(),
      vehicleIds: getSelectedVehicles(),
      reportSettings: [],
      format: '',
      showEmpty: 0,
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const getValidation = async () => {
    const responseData = await ReportService.getValidation('5');
    if (responseData) {
      setReportActionUrl(responseData?.actionUrl || '');
      setReportRange(responseData?.range || '');
      setValidation(responseData.form);

      // reportSettings from userSettings
      const reportSettingsValues = ReportService.handleReportSettings(
        responseData.userSettings,
        reportSettingsOptionsR05,
        '5'
      );
      reportSettingsValues.push('rideBook', 'basicHeader');
      setValue('reportSettings', reportSettingsValues);

      // format from userSettings
      if (responseData.userSettings && responseData.userSettings.format) {
        setValue('format', responseData.userSettings.format);
      } else {
        setValue('format', 'xlsx');
      }

      // showEmpty from userSettings
      if (responseData.userSettings) {
        setValue('showEmpty', responseData.userSettings.showEmpty ? 1 : 0);
      }
    }
  };

  const getVehicles = async () => {
    const url = `/v2/report/form/vehicleIds?reportId=${REPORT_05}`;
    setLoader(true);
    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });
    if (response && response.data && response.status === 200) {
      setLoader(false);
      const options = response.data;
      setVehicleOptions(
        options.map((i: IGetVehiclesData) => {
          i.value = String(i.value);
          return i;
        })
      );
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['rideBookValidation'],
        queryFn: () => getValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['rideBookVehicles'],
        queryFn: () => getVehicles(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  React.useEffect(() => {
    watch('reportSettings');
    if (watch('reportSettings').length === 0) {
      setBossCheckboxValue('');
    }
    if (watch('reportSettings').length === 13) {
      setBossCheckboxValue('checked');
    }
    if (watch('reportSettings').length > 0 && watch('reportSettings').length < 13) {
      setBossCheckboxValue('indeterminate');
    }
  }, [watch('reportSettings')]);

  const handleDownload = async (formValues: IRidebookDownloadData) => {
    // if previous dowbloading finished
    let finishedDownloading = false;
    if (exportData) {
      setExportData(null);
      finishedDownloading = true;
      setValuesJsons([]);
    }
    if (!finishedDownloading && valuesJsons.includes(JSON.stringify(formValues))) {
      showMessage(f({ id: 'toastr.warning' }), f({ id: 'reports.inprogress' }), 'warning');
    } else {
      const newValuesJsons = JSON.parse(JSON.stringify(valuesJsons));
      newValuesJsons.push(JSON.stringify(formValues));
      setValuesJsons(newValuesJsons);
      setLoader(true);

      const res: AxiosResponse | false = await postRequest(
        reportActionUrl.replace('/api', ''),
        formValues
      );
      props.closeCallback();
      if (res && res.data && res.data.status === 'ok') {
        setIsActiveExport(res.data.jobId);
        setIsActiveExport(res.data.jobId);
        setDownloads((oldState: IDownload[]) => {
          const job = oldState.find((item: IDownload) => item.jobId === res.data.jobId);
          if (job) {
            const updatedJob: IDownload = {
              ...job,
              isDownloaded: false,
              code: formValues.reportId,
              percentage: 0,
              generating_end: dayjs().toISOString(),
            };
            const newState = [
              ...oldState.filter((item: IDownload) => item.jobId !== res.data.jobId),
              updatedJob,
            ];
            return newState;
          }
          const newJob: IDownload = {
            jobId: res.data.jobId,
            isDownloaded: false,
            code: `R0${formValues.reportId}`,
            percentage: 0,
            generating_start: dayjs().toISOString(),
            generating_end: null,
            filename: `R0${formValues.reportId}`,
          };
          const newState = [...oldState, newJob];
          return newState;
        });
      }
    }
    setLoader(false);
  };

  const onSubmit = async (values: any) => {
    const { reportSettings, date, ...rest } = values;
    const newValues: any = { ...rest };

    const allSettingOptions = [...reportSettingsOptions.headers, ...reportSettingsOptions.others];
    allSettingOptions.forEach((item: any) => {
      newValues[item.value] = reportSettings.includes(item.value);
    });

    newValues.dateFrom = new Date(date[0]).toISOString();
    newValues.dateTo = new Date(date[1]).toISOString();
    newValues.rideBook ??= true;
    newValues.basicHeader ??= true;
    newValues.reportId = REPORT_05;
    handleDownload(newValues);
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'ridebook-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'reports.export' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  const handleReportParameter = (_value: string) => {
    if (watch('reportSettings').length === 13) {
      setValue('reportSettings', ['rideBook', 'basicHeader']);
      setBossCheckboxValue('indeterminate');
    }
    if (watch('reportSettings').length > 0 && watch('reportSettings').length < 13) {
      setValue('reportSettings', reportSettingsOptionsR05);
      setBossCheckboxValue('checked');
    }
    if (bossCheckboxValue === 'checked') {
      setValue('reportSettings', ['rideBook', 'basicHeader']);
      setBossCheckboxValue('indeterminate');
    }
  };

  return (
    <CmdModal
      id="ridebookexport-form"
      title={f({ id: 'ridebook.report.export' })}
      footerButtons={buttons}
      loader={loader}
    >
      <div className="filtered-list">
        <div style={{ marginBottom: '16px', padding: '0' }}>
          <Controller
            name="date"
            control={control}
            rules={{ required: errors.date ? errors.date.message : undefined }}
            render={({ field }) => (
              <Calendar
                {...field}
                id="date"
                name="date"
                label={f({ id: 'datepicker.range' })}
                required={true}
                allowedRange={
                  validation ? ReportService.handleAllowedRange(validation.date) : undefined
                }
                note="(dd.mm.rrrr hh:mm)"
                mode="range"
                disabledItems={['THIS_YEAR', 'PREVIOUS_YEAR']}
                error={errors.date?.message}
                touched={true}
                onChangeCallback={(value: Date | Date[]) => {
                  setValue('date', value as Date[]);
                  trigger('date');
                }}
              />
            )}
          />
        </div>
        <Controller
          name="format"
          control={control}
          rules={{ required: errors.format ? errors.format.message : undefined }}
          render={({ field }) => (
            <CmdRadioButton
              {...field}
              id="format"
              name="format"
              label={f({ id: 'reports.format' })}
              options={[
                { item: '.xlsx', value: 'xlsx' },
                { item: '.pdf', value: 'pdf' },
              ]}
              onChangeCallback={(value: string) => {
                setValue('format', value);
                trigger('format');
              }}
              isGrid={true}
            />
          )}
        />
        <Controller
          name="vehicleIds"
          control={control}
          rules={{ required: errors.vehicleIds ? errors.vehicleIds.message : undefined }}
          render={({ field }) => (
            <CmdChipSelect
              {...field}
              id="vehicleIds"
              name="vehicleIds"
              label={f({ id: 'reports.vehicleIds' })}
              placeholder={f({ id: 'form.choose' })}
              searchPlaceholder={f({ id: 'form.search' })}
              selectButton={f({ id: 'form.groupChipSelectUnique.button.apply' })}
              resetButton={f({ id: 'form.groupChipSelectUnique.button.reset' })}
              options={vehicleOptions}
              required={true}
              searchTooltip={f({
                id: 'alarms.section.vehicles.vehicleId.search.tooltip',
              })}
              selectButtonTooltip={f({
                id: 'alarms.section.vehicles.vehicleId.selectButton.tooltip',
              })}
              resetButtonTooltip={f({
                id: 'alarms.section.vehicles.vehicleId.resetButton.tooltip',
              })}
              bossCheckbox={f({ id: 'reports.chooseAllVehicles' })}
              error={errors.vehicleIds?.message}
              touched={true}
              value={watch('vehicleIds')}
              setValue={(value: string[]) => {
                setValue('vehicleIds', value);
                trigger('vehicleIds');
              }}
            />
          )}
        />
        <hr
          style={{
            marginRight: '-24px',
            marginLeft: '-24px',
            paddingTop: '10px',
            marginBottom: '3px',
            borderTop: '1px solid #ddd',
          }}
        />
        <CmdCheckbox
          id="reportSelectAll"
          name="reportSelectAll"
          label={f({ id: 'alarms.modal.timeFrame.button.selectAll' })}
          onChangeCallback={handleReportParameter}
          value={bossCheckboxValue}
        />

        <Controller
          name="reportSettings"
          control={control}
          rules={{
            required: errors.reportSettings ? errors.reportSettings.message : undefined,
          }}
          render={({ field }) => (
            <>
              <CmdCheckboxGroup
                {...field}
                key="reportSettings"
                id="reportSettings"
                name="reportSettings"
                value={watch('reportSettings')}
                label={f({ id: 'reports.tableHeader' })}
                options={reportSettingsOptions.headers || []}
                gridTemplateColumns=" 1fr 1fr "
                // bossCheckbox={f({ id: 'reports.chooseAll' })}
                required={false}
                setValue={(value: string[]) => {
                  setValue('reportSettings', value);
                  trigger('reportSettings');
                }}
              />
              <CmdCheckboxGroup
                {...field}
                key="reportSettings"
                id="reportSettings"
                name="reportSettings"
                value={watch('reportSettings')}
                label={f({ id: 'reports.parametersSetting' })}
                options={reportSettingsOptions.others || []}
                gridTemplateColumns=" 1fr 1fr "
                // bossCheckbox={f({ id: 'reports.chooseAll' })}
                required={false}
                setValue={(value: string[]) => {
                  setValue('reportSettings', value);
                  trigger('reportSettings');
                }}
              />
            </>
          )}
        />
        <hr
          style={{
            marginRight: '-24px',
            marginLeft: '-24px',
            paddingTop: '10px',
            marginBottom: '3px',
            borderTop: '1px solid #ddd',
          }}
        />
        <div
          style={{
            fontWeight: 'bold',
            fontSize: '11px',
            color: '#494949',
            marginBottom: '12px',
          }}
          className="form-group"
        >
          <span>{f({ id: 'reports.section.showEmpty.tooltip' })}</span>
        </div>
        <Controller
          name="showEmpty"
          control={control}
          rules={{
            required: errors.showEmpty ? errors.showEmpty.message : undefined,
          }}
          render={({ field }) => (
            <CmdSwitch
              {...field}
              id="showEmpty"
              label={f({ id: 'reports.showEmpty' })}
              value={watch('showEmpty')}
              setValue={(value: number) => {
                setValue('showEmpty', value);
                trigger('showEmpty');
              }}
            />
          )}
        />
      </div>
    </CmdModal>
  );
}
